import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../lib/api';

export function PasswordResetConfirm() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const resetPassword = useMutation(api.auth.resetPasswordWithToken);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Only validate token if we haven't successfully reset the password yet
  const tokenValidation = useQuery(api.auth.validateResetToken,
    token && !success ? { token } : 'skip'
  );

  useEffect(() => {
    // Don't show token errors if we've already successfully reset the password
    if (tokenValidation && !tokenValidation.valid && !success) {
      setError(tokenValidation.error || 'Token inválido o expirado');
    }
  }, [tokenValidation, success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Token no válido');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPassword({ token, newPassword });
      setSuccess(true);

      // Auto-login and redirect after successful reset
      localStorage.setItem('playerId', result.playerId);
      localStorage.setItem('playerName', result.name);
      localStorage.setItem('playerRole', result.role);

      setTimeout(() => {
        // Navigate based on role
        if (result.role === 'organizer') {
          navigate('/dashboard/organizer');
        } else if (result.role === 'administrator') {
          navigate('/dashboard/admin');
        } else {
          navigate('/dashboard/player');
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Error al restablecer la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while validating token
  if (!tokenValidation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Show error if token is invalid
  if (!tokenValidation.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img
              src="/torneopadel.png"
              alt="TorneoPadel"
              className="w-24 h-24 mx-auto mb-4 object-contain"
            />
            <h2 className="text-2xl font-bold text-red-600 mb-4">Enlace inválido o expirado</h2>
            <p className="text-gray-600 mb-6">
              {tokenValidation.error === 'Token has expired'
                ? 'Este enlace ha expirado. Por favor, solicita uno nuevo.'
                : tokenValidation.error === 'Token has already been used'
                ? 'Este enlace ya ha sido utilizado.'
                : 'Este enlace no es válido.'}
            </p>
            <Link
              to="/reset-password"
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Solicitar nuevo enlace
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-24 h-24 mx-auto mb-4 object-contain"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Crear Nueva Contraseña
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Ingresa tu nueva contraseña
          </p>
        </div>

        {success ? (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  ¡Contraseña restablecida exitosamente!
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Redirigiendo al panel...</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="new-password" className="sr-only">
                  Nueva Contraseña
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                  placeholder="Nueva Contraseña (mínimo 6 caracteres)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="sr-only">
                  Confirmar Contraseña
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-green-500 focus:border-green-500 focus:z-10 sm:text-sm"
                  placeholder="Confirmar Nueva Contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Restableciendo...' : 'Restablecer Contraseña'}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-green-600 hover:text-green-500"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}